import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useDashboardStore } from "@/stores/useDashboardStore";

import { NewsDto } from "../..";
import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../api/resources";
import Dialog from "../../../../../../components/Dialog/Dialog";
import { DialogWidth } from "../../../../../../components/Dialog/types/enums";
import { ALERT_STATUS } from "../../../../../../context/alert/types";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import useRefreshDashboardData from "../../../../../../shared/hooks/useRefreshDashboardData";
import useResponse from "../../../../../../shared/hooks/useResponse";

interface DeleteNewsDialogProps {
  isDialogOpen: boolean;
  onClose: () => void;
  newsData: NewsDto | null;
  isReleaseNotes?: boolean;
}

const DeleteNewsDialog = ({
  isDialogOpen,
  onClose,
  newsData,
  isReleaseNotes,
}: DeleteNewsDialogProps) => {
  const { deleteData } = useAxiosPrivate();
  const { handleResponse, handleCommonError } = useResponse();
  const { refreshNewsData } = useRefreshDashboardData();
  const { t } = useTranslation();

  const setScrollOffsetNumber = useDashboardStore(
    (state) => state.setNewsScrollOffset,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const deleteNewsTitle = isReleaseNotes
    ? t("Dialog##delete unpublished release notes")
    : t("Dialog##delete news");

  const deleteNewsDescription = isReleaseNotes
    ? t("Dialog##delete unpublished release notes description")
    : t("Dialog##delete news description");

  const handleSubmit = async () => {
    if (newsData) {
      try {
        setIsLoading(true);
        await deleteData(ApiResources.NewsItems, newsData.id).then(() => {
          refreshNewsData();
        });
        setScrollOffsetNumber(0);
        handleResponse(ALERT_STATUS.Success, t("Alert##news deleted"));
      } catch (error) {
        handleCommonError(error, t);
      } finally {
        setIsLoading(false);
        onClose();
      }
    }
  };

  return (
    <Dialog
      data-testid="delete-news-dialog"
      title={deleteNewsTitle}
      description={deleteNewsDescription}
      width={DialogWidth.MinWidth}
      extendToMaxWidth
      isActionsSeparator
      open={isDialogOpen}
      close={onClose}
      submit={handleSubmit}
      actions={
        <DialogActions
          onClose={onClose}
          isLoading={isLoading}
          isreadyToConfirm={true}
          confirmationButtonText={t("Button##accept")}
          confirmationButtonTestId="delete-news-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="close-delete-news"
        />
      }
    />
  );
};

export default DeleteNewsDialog;
